export type ErrorsValue = string | string[] | undefined;
export type TranslatedErrors<T extends string | number | symbol> = Partial<
    Record<T, ErrorsValue>
>;
export type JoinedTranslatedErrors<T extends string | number | symbol> =
    Partial<Record<T, string>>;
export type UntraslatedErrorParams = {
    key: string;
    // TODO: yup does not export MessageParams and obtaining it with
    // Parameters<Extract<Message, Function>> ends up with any type :(
    params: Record<string, string>;
    placeholders: Record<string, string | number>;
};
export type UntranslatedErrors =
    | Partial<Record<string, ErrorsValue | undefined>>
    | Partial<
          Record<string, UntraslatedErrorParams | UntraslatedErrorParams[]>
      >;

export const joinErrors = <T extends string>(errors: T[]) => {
    return errors
        .filter((error) => error)
        .map((error, index) => {
            if (index === 0) {
                return error;
            }
            return lowercaseFirstLetter(error);
        })
        .join(', ');
};

export type JoinErrorsFn = (errors: string[]) => string;

export const useTranslateErrors = (joinErrorsFn: JoinErrorsFn = joinErrors) => {
    const { t } = useI18n();

    // Translate signle value with and without slot
    const translateError = (
        value?: string | UntraslatedErrorParams
    ): string => {
        if (!value) return '';
        if (typeof value === 'string') return t(value);
        return t(value.key, value.placeholders);
    };

    const translateErorrs = (errors: string[]) => {
        return errors.map((error) => translateError(error));
    };

    const translateErrorsObject = <T extends UntranslatedErrors>(errors: T) => {
        const translatedErrors: TranslatedErrors<keyof T> = {};

        Object.entries(errors).forEach(([key, value]) => {
            translatedErrors[key as keyof T] = Array.isArray(value)
                ? translateErorrs(value)
                : translateError(value);
        });

        return translatedErrors;
    };

    const joinTranslateErorrs = (errors: string[]) => {
        return joinErrorsFn(errors);
    };

    const joinTranslatedErrorsObject = <T>(
        errors: TranslatedErrors<keyof T>
    ) => {
        const translatedJoinErrors: JoinedTranslatedErrors<keyof T> = {};

        Object.entries(errors).forEach(([key, value]) => {
            const castedValue = value as ErrorsValue;
            translatedJoinErrors[key as keyof T] = Array.isArray(castedValue)
                ? joinTranslateErorrs(castedValue)
                : castedValue;
        });

        return translatedJoinErrors;
    };

    return {
        joinErrors,
        translateError,
        translateErorrs,
        translateErrorsObject,
        joinTranslateErorrs,
        joinTranslatedErrorsObject,
    };
};
